// MyProductManagement.jsx — "상품 관리" 메뉴(테넌트 자기 회사 상품).
//   백엔드 /admin/ticket-types 는 이미 requesterScope 로 요청자 회사 상품만 조회/생성/수정한다.
//   운영자(role=admin)는 자기 테넌트(PINEGROUP=PINE/DOT/LNJ) 전체, 고객 테넌트는 자기 회사만.
//   운영자: 카테고리는 전역(동적 관리) 목록에서 선택. 고객 테넌트: 카테고리 API 가 의도적으로
//   비어 있어(플랫폼 사업라인 비노출) 자유 텍스트로 직접 입력한다 — 자기 상품에서 자동완성.
//   이름이 플랫폼 카테고리와 겹쳐도 정산·집계 경로가 회사 테넌트 스코프라 섞이지 않는다.
//
//   상태·API 는 useMyProducts, 화면 조각은 ProductAddRow / ProductGroups / ProductRow 로 분리했다
//   (2026-09-20, 300줄 규칙 — 분리만 하고 동작은 그대로).
import React, { useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { stdHeaderRow, stdPageTitle, stdPageSubtitle, stdOutlineBtn } from '../adminlayout/designStandard';
import { ProductSettingsModal } from './ProductSettingsModal';
import { ProductAddRow } from './ProductAddRow';
import { ProductGroups } from './ProductGroups';
import { CompanyCategoryMatchPanel } from './CompanyCategoryMatchPanel';
import { useMyProducts } from './useMyProducts';
import { useCompanyCategoryMatch } from './useCompanyCategoryMatch';

export default function MyProductManagement() {
  const ctl = useMyProducts();
  const match = useCompanyCategoryMatch();

  // ⚙️ 설정 모달 (동적 관리 > 상품 설정 화면 그대로). 닫을 때 본문 목록을 다시 읽는다.
  const [settingsOpen, setSettingsOpen] = useState(false);
  // 설정 버튼: 상품 관리 또는 동적 관리 배정자 (2026-09-20 사용자 확정 — "상품 관리 맡은 사람도").
  //   권한을 모르는 동안(로딩·실패)은 숨긴다(fail-closed). 환산장수·기본상품은 전역 데이터라
  //   서버가 운영사 소속인지 한 번 더 보고, 손님 회사에는 애초에 카테고리가 없어 내용이 비어 보인다.
  const [canManageSettings, setCanManageSettings] = useState(false);
  // 카테고리(상자) 만들기·이름수정·버리기 가능 여부 — 상품 관리 배정자도 포함
  const [canManageCategory, setCanManageCategory] = useState(false);
  // 표준 헤더(stdHeaderRow) 반응형 여백용 — 디자인 표준 통일 (2026-08-27)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // 2단 배치는 넉넉할 때만. 좁으면 한 줄로 쌓는다.
  const [isWide, setIsWide] = useState(window.innerWidth >= 1100);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsWide(window.innerWidth >= 1100);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 오른쪽 칸 선택지 = 왼쪽 칸에서 쓰는 분류들. 운영자는 전역 카테고리, 손님 회사는 자기 상품의 분류 이름.
  //   덕분에 어느 테넌트에서 보든 "왼쪽에서 만든 걸 오른쪽에서 회사에 붙인다"가 똑같이 동작한다.
  const matchOptions = (ctl.freeCategory ? ctl.myCatNames : ctl.categories.map((c) => c.name))
    .map((name) => ({ name, count: ctl.products.filter((p) => String(p.category || '').trim() === name).length }));

  useEffect(() => {
    let active = true;
    apiClient.get('/api/my/permissions').then((r) => {
      const codes = r?.ok && Array.isArray(r.data?.permCodes) ? r.data.permCodes : [];
      if (active) {
        setCanManageSettings(codes.includes('TICKET_TYPE_MANAGEMENT') || codes.includes('PRODUCT_MANAGEMENT'));
        // 카테고리 만들기·이름수정·버리기: 상품 관리 배정자도 가능 (2026-09-17). 운영사 여부는 서버가 본다.
        setCanManageCategory(codes.includes('TICKET_TYPE_MANAGEMENT') || codes.includes('PRODUCT_MANAGEMENT'));
      }
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // 폭은 다른 관리 화면과 같게 — 전에는 가운데 900px 고정이라 이 화면만 좁아 보였다 (2026-09-16 사용자 지적).
  //   휴대폰은 공통 틀(AdminLayout main)이 좌우 16px 을 준다 — 겹치지 않게 0 (2026-09-28)
  return (
    <div style={{ display: 'grid', gap: 16, ...(isMobile && { paddingInline: 0 }) }}>
      {/* 직원 관리 기준 표준 헤더 (2026-08-27 디자인 표준 통일 — 이 페이지는 헤더만 표준 정렬) */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>상품 관리</h2>
          <p style={{ ...stdPageSubtitle, margin: '4px 0 0' }}>
            {ctl.freeCategory ? '자사 상품을 등록·수정합니다. 카테고리는 직접 입력해 자유롭게 분류할 수 있습니다.' : '자사 상품을 등록·수정합니다. 카테고리·환산장수는 운영자가 관리합니다.'}
          </p>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {canManageSettings && (
            <button
              type="button"
              style={stdOutlineBtn}
              onClick={() => setSettingsOpen(true)}
              title="상품 설정 — 유효기간·환산장수·기본상품"
              aria-label="상품 설정"
            >
              ⚙️{isMobile ? '' : ' 설정'}
            </button>
          )}
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>{ctl.products.length}개</span>
        </div>
      </div>

      {/* 가끔 쓰는 설정(유효기간·환산장수·기본상품). 닫으면 본문 목록을 다시 읽어 변경을 반영. */}
      <ProductSettingsModal open={settingsOpen} onClose={() => { setSettingsOpen(false); ctl.load(); }} />

      {/* 왼쪽 = 만들기(카테고리·상품) / 오른쪽 = 회사에 붙이기.
          좁은 화면에서는 한 줄로 쌓인다 (출퇴근 관리 AttendanceDesktopLayout 과 같은 방식). */}
      <div style={{
        display: 'grid',
        gap: 16,
        alignItems: 'start',
        gridTemplateColumns: isWide ? 'minmax(0, 1fr) minmax(320px, 380px)' : 'minmax(0, 1fr)',
      }}>
        <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
          <ProductAddRow ctl={ctl} canManageCategory={canManageCategory} />
          <ProductGroups ctl={ctl} canManageCategory={canManageCategory} onCategoryChanged={() => { ctl.load(); match.load(); }} />
        </div>
        <CompanyCategoryMatchPanel match={match} options={matchOptions} />
      </div>
    </div>
  );
}
